import { DatePipe } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'utcToLocal'
})
export class UtcToLocalPipe implements PipeTransform {
  private readonly centralTimeZone = 'America/Chicago';

  constructor(public datePipe: DatePipe) {
  }

  transform(utcDateString: string | Date, format: string): string | null {
    if (!utcDateString) {
      return null;
    }

    const date = utcDateString instanceof Date
      ? utcDateString
      : this.parseUtcDate(utcDateString);

    if (!date) {
      return null;
    }

    return this.datePipe.transform(date, format, this.getTargetTimezoneOffset(date));
  }

  private parseUtcDate(utcDateString: string): Date | null {
    const hasTimezone = /([zZ]|[+\-]\d{2}:?\d{2})$/.test(utcDateString);
    const normalizedValue = hasTimezone ? utcDateString : `${utcDateString.replace(' ', 'T')}Z`;
    const date = new Date(normalizedValue);

    return Number.isNaN(date.getTime()) ? null : date;
  }

  private getTargetTimezoneOffset(date: Date): string {
    const userTimeZone = this.getUserTimeZone();
    const preferredOffset = userTimeZone ? this.tryGetTimezoneOffset(date, userTimeZone) : null;

    if (preferredOffset) {
      return preferredOffset;
    }

    return this.tryGetTimezoneOffset(date, this.centralTimeZone) ?? '-0600';
  }

  private getUserTimeZone(): string | null {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      return null;
    }
  }

  private tryGetTimezoneOffset(date: Date, timeZone: string): string | null {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      });

      const parts = formatter.formatToParts(date).reduce((accumulator, part) => {
        if (part.type !== 'literal') {
          accumulator[part.type] = part.value;
        }

        return accumulator;
      }, {} as Record<string, string>);

      const utcTimeForZone = Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
        Number(parts.second)
      );

      return this.formatOffset(Math.round((utcTimeForZone - date.getTime()) / 60000));
    } catch {
      return null;
    }
  }

  private formatOffset(totalMinutes: number): string {
    const sign = totalMinutes >= 0 ? '+' : '-';
    const absoluteMinutes = Math.abs(totalMinutes);
    const hours = Math.floor(absoluteMinutes / 60).toString().padStart(2, '0');
    const minutes = (absoluteMinutes % 60).toString().padStart(2, '0');

    return `${sign}${hours}${minutes}`;
  }
}
